import React, {useState, useEffect} from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import {useNavigate} from 'react-router-dom';
import Conversations from '../Conversations';
import './ConversationHistoryModal.css';

const ConversationHistoryModal = ({isOpen, onClose, currentConversationId, onNewChat}) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      loadRecentConversations();
    } else {
      setSearchQuery('');
    }
  }, [isOpen]);

  const parseUtcDate = (dateString) => {
    if (!dateString) return new Date(0);
    // If the string has no timezone indicator, assume UTC (backend stores UTC without 'Z')
    const normalized = /Z|[+-]\d{2}:\d{2}$/.test(dateString) ? dateString : dateString + 'Z';
    return new Date(normalized);
  };

  const loadRecentConversations = async () => {
    try {
      setLoading(true);
      const result = await Conversations.searchConversations('');
      if (result.success) {
        // Sort by most recent first
        const sorted = result.results.sort((convA, convB) => {
          const dateA = parseUtcDate(convA.updatedAt || convA.createdAt);
          const dateB = parseUtcDate(convB.updatedAt || convB.createdAt);
          return dateB - dateA;
        });
        
        // Remove duplicates by _id
        const uniqueConversations = [];
        const seenIds = new Set();
        for (const conv of sorted) {
          if (!seenIds.has(conv._id)) {
            seenIds.add(conv._id);
            uniqueConversations.push(conv);
          }
        }
        
        setConversations(uniqueConversations);
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectConversation = (conversationId) => {
    if (editingId) return; // don't navigate while editing
    navigate(`/chat?conversationId=${conversationId}`);
    onClose();
  };

  const handleEditClick = (e, conv) => {
    e.stopPropagation();
    setDeletingId(null);
    setEditingId(conv._id);
    setEditingName(conv.conversationName || '');
  };

  const handleDeleteClick = (e, convId) => {
    e.stopPropagation();
    setEditingId(null);
    setEditingName('');
    setDeletingId(convId);
  };

  const handleDeleteCancel = (e) => {
    e && e.stopPropagation();
    setDeletingId(null);
  };

  const handleDeleteConfirm = async (e, convId) => {
    e && e.stopPropagation();
    const result = await Conversations.deleteConversation(convId);
    if (result.success) {
      setConversations(prev => prev.filter(c => c._id !== convId));
    }
    setDeletingId(null);
  };

  const handleEditCancel = (e) => {
    e && e.stopPropagation();
    setEditingId(null);
    setEditingName('');
  };

  const handleEditConfirm = async (e) => {
    e && e.stopPropagation();
    const name = editingName.trim();
    if (!name || !editingId) {
      handleEditCancel();
      return;
    }
    setSavingId(editingId);
    const result = await Conversations.updateConversation(editingId, { conversationName: name });
    if (result.success) {
      setConversations(prev =>
        prev.map(c => c._id === editingId ? { ...c, conversationName: name } : c)
      );
    }
    setSavingId(null);
    setEditingId(null);
    setEditingName('');
  };

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') handleEditConfirm(e);
    if (e.key === 'Escape') handleEditCancel(e);
  };

  const displayedConversations = searchQuery.trim()
    ? conversations.filter(conv =>
        (conv.conversationName || '').toLowerCase().includes(searchQuery.toLowerCase())
      )
    : conversations.slice(0, 10);

  const handleNewChat = () => {
    onNewChat();
    onClose();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = parseUtcDate(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffSecs < 60) return 'Just now';
    if (diffMins < 60) return diffMins === 1 ? '1 minute ago' : `${diffMins} minutes ago`;
    if (diffHours < 24) return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffWeeks === 1) return 'Last week';
    if (diffWeeks < 4) return `${diffWeeks} weeks ago`;
    if (diffMonths === 1) return 'Last month';
    if (diffMonths < 12) return `${diffMonths} months ago`;
    if (diffYears === 1) return 'Last year';
    return `${diffYears} years ago`;
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="conversation-history-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Conversation History</h2>
          <button className="modal-close-button" onClick={onClose} type="button">
            <svg fill="none" height="20" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="20">
              <line x1="18" x2="6" y1="6" y2="18"/>
              <line x1="6" x2="18" y1="6" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="modal-body">
          <button className="new-chat-button" onClick={handleNewChat} type="button">
            <svg fill="none" height="16" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="16">
              <line x1="12" x2="12" y1="5" y2="19"/>
              <line x1="5" x2="19" y1="12" y2="12"/>
            </svg>
            New Conversation
          </button>

          <div className="conversation-search-wrapper">
            <svg className="conversation-search-icon" fill="none" height="16" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="16">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" x2="16.65" y1="21" y2="16.65"/>
            </svg>
            <input
              className="conversation-search-input"
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search conversations..."
              type="text"
              value={searchQuery}
            />
            {searchQuery && (
              <button
                className="conversation-search-clear"
                onClick={() => setSearchQuery('')}
                title="Clear search"
                type="button"
              >
                <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" viewBox="0 0 24 24" width="14">
                  <line x1="18" x2="6" y1="6" y2="18"/>
                  <line x1="6" x2="18" y1="6" y2="18"/>
                </svg>
              </button>
            )}
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading conversations...</p>
            </div>
          ) : displayedConversations.length === 0 ? (
            <div className="empty-state">
              <p>{searchQuery.trim() ? 'No matching conversations' : 'No conversations yet'}</p>
            </div>
          ) : (
            <div className="conversations-list">
              {displayedConversations.map((conv) => (
                <div
                  key={conv._id}
                  className={`conversation-item ${conv._id === currentConversationId ? 'active' : ''} ${editingId === conv._id ? 'editing' : ''} ${deletingId === conv._id ? 'deleting' : ''}`}
                >
                  <button
                    className="conversation-item-main"
                    onClick={() => handleSelectConversation(conv._id)}
                    type="button"
                    disabled={editingId === conv._id || deletingId === conv._id}
                  >
                    <div className="conversation-info">
                      {editingId === conv._id ? (
                        <input
                          autoFocus
                          className="conversation-rename-input"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onKeyDown={handleEditKeyDown}
                          onClick={(e) => e.stopPropagation()}
                          type="text"
                        />
                      ) : (
                        <div className="conversation-name">
                          {conv.conversationName || 'Untitled Conversation'}
                        </div>
                      )}
                      {editingId !== conv._id && conv.messages?.[0]?.text && (
                        <div className="conversation-preview">
                          {conv.messages[0].text.substring(0, 60)}
                          {conv.messages[0].text.length > 60 ? '...' : ''}
                        </div>
                      )}
                    </div>
                    <div
                      className="conversation-date"
                      title={parseUtcDate(conv.updatedAt || conv.createdAt).toLocaleString()}
                    >
                      {formatDate(conv.updatedAt || conv.createdAt)}
                    </div>
                  </button>

                  <div className="conversation-actions">
                    {editingId === conv._id ? (
                      <>
                        <button
                          className="conv-action-btn conv-action-confirm"
                          onClick={handleEditConfirm}
                          disabled={savingId === conv._id}
                          type="button"
                          title="Confirm"
                        >
                          {savingId === conv._id ? (
                            <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="14">
                              <circle cx="12" cy="12" r="10"/>
                            </svg>
                          ) : (
                            <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" viewBox="0 0 24 24" width="14">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          )}
                        </button>
                        <button
                          className="conv-action-btn conv-action-cancel"
                          onClick={handleEditCancel}
                          type="button"
                          title="Cancel"
                        >
                          <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" viewBox="0 0 24 24" width="14">
                            <line x1="18" x2="6" y1="6" y2="18"/>
                            <line x1="6" x2="18" y1="6" y2="18"/>
                          </svg>
                        </button>
                      </>
                    ) : deletingId === conv._id ? (
                      <>
                        <button
                          className="conv-action-btn conv-action-confirm conv-action-delete-confirm"
                          onClick={(e) => handleDeleteConfirm(e, conv._id)}
                          type="button"
                          title="Confirm delete"
                        >
                          <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" viewBox="0 0 24 24" width="14">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        </button>
                        <button
                          className="conv-action-btn conv-action-cancel"
                          onClick={handleDeleteCancel}
                          type="button"
                          title="Cancel"
                        >
                          <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" viewBox="0 0 24 24" width="14">
                            <line x1="18" x2="6" y1="6" y2="18"/>
                            <line x1="6" x2="18" y1="6" y2="18"/>
                          </svg>
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="conv-action-btn conv-action-edit"
                          onClick={(e) => handleEditClick(e, conv)}
                          type="button"
                          title="Rename"
                        >
                          <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="14">
                            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                        </button>
                        <button
                          className="conv-action-btn conv-action-delete"
                          onClick={(e) => handleDeleteClick(e, conv._id)}
                          type="button"
                          title="Delete"
                        >
                          <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="14">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                            <path d="M10 11v6"/>
                            <path d="M14 11v6"/>
                            <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

ConversationHistoryModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentConversationId: PropTypes.string,
  onNewChat: PropTypes.func.isRequired
};

export default ConversationHistoryModal;
